import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
	Autocomplete,
	Box,
	Button,
	CircularProgress,
	Link,
	Stack,
	Typography,
	TextField,
} from '@mui/material';
import {
	fetchPlaceDetails,
	fetchPlaceSuggestions,
} from '../Shared/Api';
import LocationMapPicker from './LocationMapPicker';
import Modal from './Modals/Modal';

const newSessionToken = () => {
	if (typeof crypto !== 'undefined' && crypto.randomUUID) {
		return crypto.randomUUID();
	}
	return `sess-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const roundCoord = (value) => {
	if (value == null || value === '') return '';
	const n = Number(value);
	if (Number.isNaN(n)) return '';
	return String(Number(n.toFixed(6)));
};

const hasCoords = (lat, lng) =>
	lat != null &&
	lat !== '' &&
	lng != null &&
	lng !== '' &&
	Number.isFinite(Number(lat)) &&
	Number.isFinite(Number(lng));

/**
 * Address search with optional map pin (opened in a modal).
 * Backend geocodes address on save when coordinates are omitted.
 */
const AddressAutocomplete = ({
	label = 'Address',
	value = '',
	latitude = '',
	longitude = '',
	onChange,
	disabled = false,
	required = false,
	dense = false,
	autoFocus = false,
	textFieldProps = {},
}) => {
	const [inputValue, setInputValue] = useState(value || '');
	const [options, setOptions] = useState([]);
	const [loading, setLoading] = useState(false);
	const [resolving, setResolving] = useState(false);
	const [error, setError] = useState('');
	const [mapOpen, setMapOpen] = useState(false);
	const [draftLat, setDraftLat] = useState(latitude);
	const [draftLng, setDraftLng] = useState(longitude);
	const sessionTokenRef = useRef(newSessionToken());
	const debounceRef = useRef(null);
	const requestIdRef = useRef(0);

	useEffect(() => {
		setInputValue(value || '');
	}, [value]);

	const emit = useCallback(
		(next) => {
			onChange?.({
				address: next.address ?? inputValue,
				latitude: next.latitude ?? latitude,
				longitude: next.longitude ?? longitude,
			});
		},
		[inputValue, latitude, longitude, onChange],
	);

	const loadSuggestions = useCallback(async (text) => {
		const trimmed = (text || '').trim();
		if (trimmed.length < 3) {
			setOptions([]);
			return;
		}
		const requestId = ++requestIdRef.current;
		setLoading(true);
		setError('');
		try {
			const response = await fetchPlaceSuggestions({
				input: trimmed,
				session_token: sessionTokenRef.current,
			});
			if (requestId !== requestIdRef.current) return;
			setOptions(response?.suggestions || []);
		} catch (err) {
			if (requestId !== requestIdRef.current) return;
			setOptions([]);
			setError(err.message || 'Could not load address suggestions.');
		} finally {
			if (requestId === requestIdRef.current) {
				setLoading(false);
			}
		}
	}, []);

	const handleInputChange = (_event, nextValue, reason) => {
		setInputValue(nextValue);
		if (reason === 'input' || reason === 'clear') {
			emit({ address: nextValue, latitude: '', longitude: '' });
			if (debounceRef.current) clearTimeout(debounceRef.current);
			debounceRef.current = setTimeout(() => {
				loadSuggestions(nextValue);
			}, 250);
		}
	};

	const handleSelect = async (_event, option) => {
		if (!option || typeof option === 'string') {
			return;
		}
		setResolving(true);
		setError('');
		try {
			const details = await fetchPlaceDetails({
				place_id: option.place_id,
				session_token: sessionTokenRef.current,
			});
			sessionTokenRef.current = newSessionToken();
			const address = details.address || option.description;
			setInputValue(address);
			setOptions([]);
			onChange?.({
				address,
				latitude: roundCoord(details.latitude),
				longitude: roundCoord(details.longitude),
			});
		} catch (err) {
			setError(err.message || 'Could not load place details.');
		} finally {
			setResolving(false);
		}
	};

	const openMap = () => {
		setDraftLat(latitude);
		setDraftLng(longitude);
		setMapOpen(true);
	};

	const closeMap = () => {
		setMapOpen(false);
	};

	const applyMapPin = () => {
		emit({
			address: inputValue,
			latitude: roundCoord(draftLat),
			longitude: roundCoord(draftLng),
		});
		setMapOpen(false);
	};

	const fieldSize = dense ? 'small' : undefined;
	const pinSet = hasCoords(latitude, longitude);

	return (
		<>
			<Stack spacing={0.5}>
				<Autocomplete
					freeSolo
					filterOptions={(x) => x}
					options={options}
					loading={loading || resolving}
					disabled={disabled}
					inputValue={inputValue}
					onInputChange={handleInputChange}
					onChange={handleSelect}
					getOptionLabel={(option) =>
						typeof option === 'string'
							? option
							: option.description || option.main_text || ''
					}
					isOptionEqualToValue={(option, val) =>
						option?.place_id && val?.place_id
							? option.place_id === val.place_id
							: option === val
					}
					renderOption={(props, option) => (
						<Box component="li" {...props} key={option.place_id}>
							<Box sx={{ minWidth: 0 }}>
								<Typography variant="body2" noWrap>
									{option.main_text || option.description}
								</Typography>
								{option.secondary_text ? (
									<Typography
										variant="caption"
										color="text.secondary"
										noWrap
									>
										{option.secondary_text}
									</Typography>
								) : null}
							</Box>
						</Box>
					)}
					renderInput={(params) => (
						<TextField
							{...params}
							{...textFieldProps}
							label={label || undefined}
							placeholder={
								textFieldProps.placeholder ||
								(label ? undefined : 'Search address…')
							}
							required={required}
							size={fieldSize}
							autoFocus={autoFocus}
							error={Boolean(error)}
							helperText={
								error || (
									<>
										{pinSet ? 'Pin set · ' : null}
										<Link
											component="button"
											type="button"
											underline="hover"
											onClick={openMap}
											disabled={disabled}
											sx={{
												verticalAlign: 'baseline',
												fontSize: 'inherit',
											}}
										>
											{pinSet ? 'Adjust pin on map' : 'Set pin on map'}
										</Link>
									</>
								)
							}
							InputProps={{
								...params.InputProps,
								...textFieldProps.InputProps,
								endAdornment: (
									<>
										{loading || resolving ? (
											<CircularProgress color="inherit" size={18} />
										) : null}
										{params.InputProps.endAdornment}
									</>
								),
							}}
						/>
					)}
				/>
			</Stack>

			<Modal
				open={mapOpen}
				onClose={closeMap}
				modalTitle="Set location pin"
				maxWidth="sm"
				alwaysMaxWidth
				useActionButtons
				showCancelButton
				closeButtonName="Cancel"
				actionButtons={
					<Button
						variant="contained"
						onClick={applyMapPin}
						disabled={!hasCoords(draftLat, draftLng)}
					>
						Use this pin
					</Button>
				}
				overrideSx={{ pt: 1 }}
			>
				<Stack spacing={1.5}>
					<Typography variant="body2" color="text.secondary">
						Drop a pin for off-grid or hard-to-find sites. Your address label
						stays as typed — this only sets coordinates.
					</Typography>
					{mapOpen ? (
						<LocationMapPicker
							latitude={draftLat}
							longitude={draftLng}
							height={360}
							onChange={({ latitude: lat, longitude: lng }) => {
								setDraftLat(roundCoord(lat));
								setDraftLng(roundCoord(lng));
							}}
						/>
					) : null}
				</Stack>
			</Modal>
		</>
	);
};

export default AddressAutocomplete;
